import { buildTranslate } from 'locales'
import React from 'react'
import ConfigRowModal from './ConfigRowModal'
import LocationModePicker from './LocationModePicker'
import { useActiveLocationsController } from './useActiveLocationsController'

const t = buildTranslate('recovery_ai.settings.active_locations')

type Props = {
  /** Namespaces the picker's radio `name` + helper id (C1), so this dialog can
   *  co-mount with the Settings pane without stealing its radio selection. */
  instanceId: string
  onClose: () => void
  onSaved: () => void
  /** Host width override, forwarded to the shared shell. */
  width?: string
}

/**
 * The active-locations config-modal row. Locations cannot ride the draft
 * context — they persist via PUT recovery/location_configs — so this row owns a
 * controller that satisfies the save adapter directly.
 *
 *   context: built on LocationModePicker, never on ActiveLocationsPane. The
 *   pane short-circuits to its "locked" copy while !onboarding_completed, which
 *   is precisely the pre-activation window this modal exists to make editable —
 *   hence `skipWhenNotOnboarded: false` and the ignored `isLocked`. The pane's
 *   gutter, pilot banner, route-leave <Prompt> and inline Save are all chrome
 *   the modal shell replaces (L2).
 */
const ActiveLocationsConfigRow = ({
  instanceId,
  onClose,
  onSaved,
  width,
}: Props) => {
  const locations = useActiveLocationsController({
    skipWhenNotOnboarded: false,
  })

  return (
    <ConfigRowModal
      rowKey="active_locations"
      width={width}
      adapter={locations}
      load={{
        isLoading: locations.isLoading,
        isLoadError: locations.isFetchError,
      }}
      // The per-location write is sequenced first, so a config-only failure
      // means the selection DID land — say so, rather than letting the shell's
      // generic copy imply it was lost.
      saveErrorMessage={
        locations.isConfigOnlyError ? t('save_error_config_only') : undefined
      }
      // No onSaveAndTest: Save-and-test belongs to the Agent Tuning tab, which
      // never lists this row, and testing from the activation funnel would eject
      // the merchant out of it (L2).
      onClose={onClose}
      onSaved={onSaved}
    >
      <LocationModePicker
        mode={locations.mode}
        onModeChange={locations.onModeChange}
        locations={locations.locations}
        selectedIds={locations.selectedIds}
        onToggle={locations.onToggle}
        onRemove={locations.onRemove}
        selectedLocations={locations.selectedLocations}
        canManage={locations.canManage}
        instanceId={instanceId}
        labels={locations.labels}
      />
    </ConfigRowModal>
  )
}

export default ActiveLocationsConfigRow
